import { effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { AppError, SpinnerService } from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs';

import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@kr/core';
import { Transfer } from '@kr/models';

import { TRANSFER_API } from './api/transfer-api.interface';
import { TransferState } from './models/store.model';

const initialState: TransferState = {
  transaction: null,
  payOutRes: null,
};

export const TransferStore = signalStore(
  withDevtools('transfer'),
  withState(initialState),
  withRequestStatus(['getEstimatesTransferInternal', 'getEstimatesTransferExternal', 'executeTransferInternal', 'executeTransferExternal']),
  withRequestHandler(),
  withProps(store => ({
    store,
    transferApi: inject(TRANSFER_API),
    storeHelperService: inject(StoreHelperService),
    spinnerService: inject(SpinnerService),
  })),
  withMethods(({ store, transferApi }) => ({
    getEstimatesTransferInternal: rxMethod<{
      body: Transfer;
      orgId: number;
    }>(
      switchMap(({ body, orgId }) => {
        patchState(store, setPendingStatusFor('getEstimatesTransferInternal'));
        return transferApi.getEstimatesTransferInternal(body, orgId).pipe(
          tapResponse({
            next: transaction => {
              patchState(store, { transaction }, setSuccessStatusFor('getEstimatesTransferInternal'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getEstimatesTransferInternal', error)),
          }),
        );
      }),
    ),
    getEstimatesTransferExternal: rxMethod<{
      body: Transfer;
      orgId: number;
    }>(
      switchMap(({ body, orgId }) => {
        patchState(store, setPendingStatusFor('getEstimatesTransferExternal'));
        return transferApi.getEstimatesTransferExternal(body, orgId).pipe(
          tapResponse({
            next: transaction => {
              patchState(store, { transaction }, setSuccessStatusFor('getEstimatesTransferExternal'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('getEstimatesTransferExternal', error)),
          }),
        );
      }),
    ),
    executeTransferInternal: rxMethod<{
      body: Transfer;
      orgId: number;
    }>(
      switchMap(({ body, orgId }) => {
        patchState(store, setPendingStatusFor('executeTransferInternal'));
        return transferApi.executeTransferInternal(body, orgId).pipe(
          tapResponse({
            next: payOutRes => {
              patchState(store, { payOutRes }, setSuccessStatusFor('executeTransferInternal'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('executeTransferInternal', error)),
          }),
        );
      }),
    ),
    executeTransferExternal: rxMethod<{
      body: Transfer;
      orgId: number;
    }>(
      switchMap(({ body, orgId }) => {
        patchState(store, setPendingStatusFor('executeTransferExternal'));
        return transferApi.executeTransferExternal(body, orgId).pipe(
          tapResponse({
            next: payOutRes => {
              patchState(store, { payOutRes }, setSuccessStatusFor('executeTransferExternal'));
            },
            error: (error: AppError) => patchState(store, setErrorStatusFor('executeTransferExternal', error)),
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.spinnerService.observe(toObservable(store.isLoading));
      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);
